import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FitiPublication, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const PUBLISHER_ROLES = ['SFA_LICENSING', 'QUOTA_MANAGER'];

/** M32 Fisheries — FiTI (Fisheries Transparency Initiative) annual publications, auto-published to C11 OpenGov. */
export function Fiti() {
  const { persona } = useAuth();
  const isPublisher = persona ? persona.roles.some((r) => PUBLISHER_ROLES.includes(r)) : false;
  const [tab, setTab] = useState<'PUBLISHED' | 'DRAFT'>('PUBLISHED');
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => `/api/v1/m32/fiti?limit=50&status=${tab}`, [tab]);
  const listQ = useApiQuery<Page<FitiPublication>>(path);

  return (
    <div>
      <PageHeader
        title="FiTI Transparency Publications"
        subtitle="Annual fisheries transparency datasets — publishing notifies C11 OpenGov (M32)."
        actions={
          isPublisher ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Draft new publication
            </button>
          ) : undefined
        }
      />
      {isPublisher ? (
        <TabStrip tabs={[{ key: 'PUBLISHED', label: 'Published' }, { key: 'DRAFT', label: 'Drafts' }]} active={tab} onChange={setTab} />
      ) : null}
      {!isPublisher ? <RoleNotice roles={PUBLISHER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No publications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Period</th>
                <th className="table-head">Title</th>
                <th className="table-head">Published</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedPeriod(f.period)}>
                  <td className="table-cell font-medium">{f.period}</td>
                  <td className="table-cell">{f.title}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(f.publishedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedPeriod !== null ? (
        <FitiDetailModal
          period={selectedPeriod}
          isPublisher={isPublisher}
          onClose={() => setSelectedPeriod(null)}
          onChanged={() => {
            setSelectedPeriod(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewFitiModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FitiDetailModal({ period, isPublisher, onClose, onChanged }: { period: number; isPublisher: boolean; onClose: () => void; onChanged: () => void }) {
  const fq = useApiQuery<FitiPublication>(`/api/v1/m32/fiti/${period}`);
  const f = fq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publish = async () => {
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m32/fiti/${f.id}/publish`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={f ? `${f.title} (${f.period})` : 'FiTI publication'} onClose={onClose} wide>
      {fq.error ? <ErrorNote message={fq.error} /> : null}
      {fq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {f ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Detail label="Status" value={<StatusBadge status={f.status} />} />
            <Detail label="Published" value={formatDateTime(f.publishedAt)} />
            <Detail label="OpenGov notice ref" value={f.openGovNoticeRef ?? '—'} mono />
          </div>
          <Detail label="Summary" value={f.summary ?? '—'} />
          {f.datasetJson ? (
            <div>
              <div className="field-label">Computed dataset</div>
              <pre className="max-h-56 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-emerald-300">{JSON.stringify(f.datasetJson, null, 2)}</pre>
            </div>
          ) : null}
          {isPublisher && f.status === 'DRAFT' ? (
            <div className="border-t border-slate-100 pt-4">
              <button className="btn-success" disabled={busy} onClick={publish}>
                {busy ? 'Publishing…' : 'Recompute & publish to OpenGov'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewFitiModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/fiti', { period: Number.parseInt(period, 10), title, summary: summary || undefined });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft FiTI publication" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Period (year)">
          <input className="field-input" type="number" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </Field>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Seychelles Fisheries Transparency Report" />
        </Field>
        <Field label="Summary (optional)">
          <textarea className="field-input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !title} onClick={submit}>
          {busy ? 'Drafting…' : 'Draft publication'}
        </button>
      </div>
    </Modal>
  );
}
